// Country-pack governance (Platform Operations Console P2). Read-only view of the
// statutory packs (PAYE/NSSF/SDL/…) that drive payroll for each jurisdiction:
// versions, effective dates, active status, required statutory identifiers, and the
// rule detail (progressive bands / rates). Authoring the law stays out-of-band
// (seed migration + local tax sign-off) — this is visibility, not a rate editor.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Tag, Button, Drawer, Typography, Space, Alert, Descriptions, Empty, message, Card, Upload,
} from 'antd';
import { ReloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, StatusTag } from '../../components/console';
import { useAppSelector } from '../../app/hooks';
import { selectRoles } from '../../features/auth/authSlice';
import CountryPackEditor from './CountryPackEditor';
import {
  listCountryPacks, getCountryPack, importCountryPack,
  type CountryPack, type CountryPackDetail, type PackRule, type PackBand, type PackStatus,
} from '../../services/countryPackService';

const { Text } = Typography;

const AUTHOR_ROLES = ['tax_officer', 'platform_admin', 'platform_owner', 'super_admin', 'system_admin'];
const PUBLISH_ROLES = ['platform_admin', 'platform_owner', 'super_admin', 'system_admin'];

// Map a pack lifecycle status onto a StatusTag semantic status.
const statusView = (p: CountryPack): { status: string; label: string } => {
  const s: PackStatus = p.status ?? (p.active ? 'published' : 'superseded');
  if (s === 'published') return { status: 'active', label: 'Published' };
  if (s === 'draft') return { status: 'draft', label: 'Draft' };
  if (s === 'in_review') return { status: 'review', label: 'In review' };
  return { status: 'inactive', label: 'Superseded' };
};

type EditorMode =
  | { kind: 'create'; country: string; cloneFromId?: string }
  | { kind: 'edit'; id: string }
  | null;

const pct = (s?: string) => (s == null || s === '' ? '—' : `${(Number(s) * 100).toFixed(2)}%`);

// Compact human summary of a rule's rate/bands for the rules table.
const ruleAmount = (r: PackRule): string => {
  if (r.kind === 'bracket') return `progressive · ${r.config.bands?.length ?? 0} bands`;
  if (r.kind === 'percent') return pct(r.config.rate);
  if (r.kind === 'fixed') return r.config.amount ?? '—';
  if (r.kind === 'formula') return 'formula';
  return '—';
};

const bandColumns: ColumnsType<PackBand> = [
  { title: 'From', dataIndex: 'from', render: (v: string) => Number(v).toLocaleString() },
  { title: 'To', dataIndex: 'to', render: (v: string | null) => (v == null ? '∞' : Number(v).toLocaleString()) },
  { title: 'Rate', dataIndex: 'rate', render: (v: string) => pct(v) },
];

export function CountryPacksPage() {
  const [packs, setPacks] = useState<CountryPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detail, setDetail] = useState<CountryPackDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>(null);

  const roles = useAppSelector(selectRoles);
  const canAuthor = roles.some((r) => AUTHOR_ROLES.includes(r));
  const canPublish = roles.some((r) => PUBLISH_ROLES.includes(r));

  const load = async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      setPacks(await listCountryPacks());
    } catch {
      if (initial) message.error('Could not load country packs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { void load(true); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetail({ id } as CountryPackDetail); // open the drawer immediately
    try {
      setDetail(await getCountryPack(id));
    } catch {
      message.error('Could not load pack detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Group packs by country so each jurisdiction reads as one card.
  const byCountry = useMemo(() => {
    const m = new Map<string, CountryPack[]>();
    for (const p of packs) {
      const arr = m.get(p.country) ?? [];
      arr.push(p);
      m.set(p.country, arr);
    }
    return Array.from(m.entries());
  }, [packs]);

  const versionColumns: ColumnsType<CountryPack> = [
    { title: 'Version', dataIndex: 'version', width: 90, render: (v: number) => <Text strong>v{v}</Text> },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Effective', width: 200, render: (_: unknown, p) => `${fmtDate(p.effective_from)} → ${p.effective_to ? fmtDate(p.effective_to) : 'open'}` },
    { title: 'Rules', dataIndex: 'rule_count', width: 80 },
    {
      title: 'Status', width: 120,
      render: (_: unknown, p) => { const v = statusView(p); return <StatusTag status={v.status} label={v.label} />; },
    },
    {
      title: '', width: 200, align: 'right' as const,
      render: (_: unknown, p) => {
        const s = p.status ?? (p.active ? 'published' : 'superseded');
        return (
          <Space size={4}>
            {canAuthor && s === 'draft' && (
              <Button size="small" type="link" onClick={() => setEditorMode({ kind: 'edit', id: p.id })}>Edit</Button>
            )}
            {s === 'in_review' && (
              <Button size="small" type="link" onClick={() => setEditorMode({ kind: 'edit', id: p.id })}>
                {canPublish ? 'Review & publish' : 'Review'}
              </Button>
            )}
            <Button size="small" type="link" onClick={() => void openDetail(p.id)}>View rules</Button>
          </Space>
        );
      },
    },
  ];

  const ruleColumns: ColumnsType<PackRule> = [
    { title: '#', dataIndex: 'order_index', width: 56 },
    { title: 'Code', dataIndex: 'code', render: (c: string) => <Text code>{c}</Text> },
    { title: 'Name', render: (_: unknown, r) => r.config.name || '—' },
    { title: 'Payer', dataIndex: 'payer', width: 100, render: (p: string) => <Tag>{p}</Tag> },
    { title: 'Type', dataIndex: 'kind', width: 110 },
    { title: 'Basis', width: 130, render: (_: unknown, r) => <Text type="secondary">{r.config.basis || '—'}</Text> },
    { title: 'Rate / bands', align: 'right' as const, render: (_: unknown, r) => ruleAmount(r) },
  ];

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Country packs"
        subtitle="Statutory packs (PAYE, NSSF, SDL…) that drive payroll per jurisdiction"
        actions={
          <Space>
            {canAuthor && (
              <Upload
                accept=".yaml,.yml,.json"
                showUploadList={false}
                beforeUpload={(f) => {
                  void (async () => {
                    try {
                      const { id } = await importCountryPack(f as File);
                      message.success('Imported as a draft');
                      await load(false);
                      setEditorMode({ kind: 'edit', id });
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || 'Import failed');
                    }
                  })();
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />}>Import pack</Button>
              </Upload>
            )}
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>
              Refresh
            </Button>
          </Space>
        }
      />

      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message="Governed statutory reference"
        description={
          canAuthor
            ? 'These are the platform default packs every tenant files under. Changes are made in-platform as a new future-dated version, require a tax sign-off document, and a second platform admin to publish (never editable by tenants).'
            : 'These are the platform default packs shared by every tenant — the statutory law a tenant files under, never edited by tenants. Authoring requires the tax_officer or a platform admin role.'
        }
      />

      {loading ? (
        <Card loading />
      ) : byCountry.length === 0 ? (
        <Empty description="No country packs." />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {byCountry.map(([country, versions]) => {
            const active = versions.find((v) => (v.status ?? (v.active ? 'published' : '')) === 'published') ?? versions[0];
            const hasOpenDraft = versions.some((v) => v.status === 'draft' || v.status === 'in_review');
            return (
              <Card
                key={country}
                title={<Space><Text strong>{country}</Text><Text type="secondary">{active?.name}</Text></Space>}
                extra={canAuthor && !hasOpenDraft && (
                  <Button size="small" icon={<PlusOutlined />}
                    onClick={() => setEditorMode({ kind: 'create', country, cloneFromId: active?.id })}>
                    New version
                  </Button>
                )}
              >
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={versions}
                  columns={versionColumns}
                />
              </Card>
            );
          })}
        </Space>
      )}

      <Drawer
        title={detail ? `${detail.country} · ${detail.name ?? ''} v${detail.version ?? ''}` : 'Pack'}
        width={720}
        open={!!detail}
        onClose={() => setDetail(null)}
        loading={detailLoading}
      >
        {detail && detail.rules && (
          <>
            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Country">{detail.country}</Descriptions.Item>
              <Descriptions.Item label="Version">v{detail.version}</Descriptions.Item>
              <Descriptions.Item label="Effective from">{fmtDate(detail.effective_from)}</Descriptions.Item>
              <Descriptions.Item label="Effective to">{detail.effective_to ? fmtDate(detail.effective_to) : 'open'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <StatusTag status={detail.active ? 'active' : 'inactive'} />
              </Descriptions.Item>
              <Descriptions.Item label="Rules">{detail.rule_count}</Descriptions.Item>
            </Descriptions>

            {detail.identifier_schema && (detail.identifier_schema.employee || detail.identifier_schema.employer) && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Required statutory identifiers</Text>
                <div style={{ marginTop: 6 }}>
                  {(['employee', 'employer'] as const).map((who) => {
                    const fields = detail.identifier_schema[who];
                    if (!fields) return null;
                    return (
                      <div key={who} style={{ marginBottom: 4 }}>
                        <Text type="secondary" style={{ textTransform: 'capitalize' }}>{who}: </Text>
                        {Object.entries(fields).map(([key, f]) => (
                          <Tag key={key} color={f.required ? 'blue' : undefined}>
                            {f.label || key}{f.required ? ' *' : ''}
                          </Tag>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Text strong>Rules</Text>
            <Table
              style={{ marginTop: 8 }}
              rowKey="code"
              size="small"
              pagination={false}
              dataSource={detail.rules}
              columns={ruleColumns}
              expandable={{
                rowExpandable: (r) => r.kind === 'bracket' && !!r.config.bands?.length,
                expandedRowRender: (r) => (
                  <Table
                    rowKey={(b) => b.from}
                    size="small"
                    pagination={false}
                    dataSource={r.config.bands}
                    columns={bandColumns}
                  />
                ),
              }}
            />
          </>
        )}
      </Drawer>

      <CountryPackEditor
        mode={editorMode}
        canPublish={canPublish}
        onClose={() => setEditorMode(null)}
        onSaved={() => { setEditorMode(null); void load(false); }}
      />
    </div>
  );
}

export default CountryPacksPage;
